import { HttpErrorResponse, HttpHandlerFn, HttpHeaders, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { CbreLoggerToken } from '@cbre/logger';
import { throwError, timer } from 'rxjs';
import { catchError, retry, tap } from 'rxjs/operators';
import { v4 as uuidV4 } from 'uuid';
import { IMPERSONATE_AUTH_TOKEN, isImpersonationModeOn, USERID_TOKEN } from '@miq/utils';
import { CbreHttpConfigToken } from '../cbre-http.config';
import { ErrorFactory } from '../errors/error-factory.service';
import { X_CBRE_REQUEST_ID_HEADER } from '../utils/constants';
import { mergeHttpHeaders } from '../utils/merge-headers.service';

const CIQ_RETRY_DELAY_MS = 2000;
const CIQ_RETRY_MAX_ATTEMPTS = 3;
const CIQ_AUTH_ENDPOINTS = ['ciq/services', 'ciq/opportunity', 'ciq/lead', 'ciq/contacts'];

function isCiqAuthRequest(url: string): boolean {
  return !url.includes('ciq/token') && CIQ_AUTH_ENDPOINTS.some(ep => url.includes(ep));
}

function getCiqUserId(): string {
  const isImpersonatedModeOn = isImpersonationModeOn();
  const userIdToken = localStorage.getItem(isImpersonatedModeOn ? IMPERSONATE_AUTH_TOKEN : USERID_TOKEN);
  return userIdToken ?? '';
}

// CIQ proxy requests carry only the user's MIQ session token; the backend now
// sources the Salesforce token + instance URL server-side, so the CIQAuthorization
// and instanceUrl headers are no longer sent from the browser (CM-30250).
function buildCiqHeaders(): HttpHeaders {
  return new HttpHeaders({
    'Access-Control-Allow-Origin': '*',
    Authorization: `BEARER ${getCiqUserId()}`,
  });
}

/**
 * Intercepts all http requests and adds stats (when request starts and when it finishes) and
 * binds transactional and span-ing identifiers.
 * In case of any error it captures the error headers and adds captured stats to HttpErrorResponse
 * logs it. if Debug is enabled it also debugs the stats information to configured transporters.
 * For CIQ auth endpoints, injects auth headers and retries up to CIQ_RETRY_MAX_ATTEMPTS times.
 */
export const httpSpanInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const config = inject(CbreHttpConfigToken);
  const logger = inject(CbreLoggerToken);
  const traceHeaderName = config.requestIdHeaderName ?? X_CBRE_REQUEST_ID_HEADER;

  if (config.skipUrlInterception.some(urlPattern => request.url.includes(urlPattern))) {
    return next(request);
  }

  const timeZoneOffset = ((new Date().getTimezoneOffset() * -1) / 60).toPrecision(2).toString();
  const requestIDStartTs = Date.now();
  const requestID = uuidV4(`${request.url}_${requestIDStartTs}`);
  const isCiq = isCiqAuthRequest(request.url);

  const spanHeaders = request.headers
    .set(`${traceHeaderName}-StartTs`, requestIDStartTs.toString())
    .set(traceHeaderName, requestID)
    .set('time-zone', timeZoneOffset);

  const outgoingHeaders = isCiq ? mergeHttpHeaders(spanHeaders, buildCiqHeaders()) : spanHeaders;

  const nextRequest = request.clone({ headers: outgoingHeaders });

  const call$ = next(nextRequest).pipe(
    tap(() =>
      logger.info({
        eventName: 'request finished',
        requestID,
        body: request.body,
        requestUrl: request.url,
        requestIDStartTs,
        requestIDEndTs: Date.now(),
      })
    )
  );

  return (
    isCiq
      ? call$.pipe(
          retry({
            count: CIQ_RETRY_MAX_ATTEMPTS,
            delay: (_, retryCount) => {
              logger.info(`Retrying ${request.url}. Retry count ${retryCount}`);
              return timer(CIQ_RETRY_DELAY_MS);
            },
          })
        )
      : call$
  ).pipe(
    catchError((error: HttpErrorResponse) => {
      const nextHeaders = mergeHttpHeaders(
        outgoingHeaders,
        error.headers,
        new HttpHeaders({ [`${traceHeaderName}-EndTs`]: Date.now().toString() })
      );
      const categorizedError = ErrorFactory.GetError({ ...error, headers: nextHeaders });
      logger.error({
        eventName: 'request failed',
        requestID,
        requestUrl: request.url,
        status: error.status,
        statusText: error.statusText,
        message: error.message,
      });
      return throwError(() => categorizedError);
    })
  );
};
